// Dialog de diff entre a versão ABERTA no editor e qualquer outra
// versão do template (§4.5). Diff simples por linha (diff-lines.ts),
// unificado: linhas removidas (versão comparada) em vermelho,
// adicionadas (versão aberta) em verde.
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type { NotificationTemplateVersionType } from "@/types/notification-center"

import {
  computeLineDiff,
  summarizeDiff,
} from "../diff-lines"

const editorPrefix =
  "pages.notificationCenter.templateEditorPage"

type TemplateDiffDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  // Versão aberta no editor (lado "novo" do diff) — usa o source
  // em edição para o diff refletir mudanças não salvas.
  currentVersion: number
  currentSource: string
  versions: NotificationTemplateVersionType[]
}

export const TemplateDiffDialog = ({
  open,
  onOpenChange,
  currentVersion,
  currentSource,
  versions,
}: TemplateDiffDialogProps) => {
  const { t } = useTranslation()
  const otherVersions = versions.filter(
    (item) => item.version !== currentVersion
  )
  const [compareWith, setCompareWith] = useState<
    string | undefined
  >(otherVersions[0]?.version.toString())

  const compared = otherVersions.find(
    (item) => item.version.toString() === compareWith
  )

  // Sem useMemo: o diff é barato p/ sources de template e o React
  // Compiler do repo não preserva memoização manual aqui.
  const diff = compared
    ? computeLineDiff(compared.source, currentSource)
    : []
  const summary = summarizeDiff(diff)
  const hasChanges =
    summary.added > 0 || summary.removed > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t(`${editorPrefix}.diff.title`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${editorPrefix}.diff.description`, {
              version: currentVersion,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          <label
            className="text-sm text-muted-foreground"
            htmlFor="diff-compare-with"
          >
            {t(`${editorPrefix}.diff.compareWith`)}
          </label>
          <Select
            value={compareWith}
            onValueChange={setCompareWith}
          >
            <SelectTrigger
              id="diff-compare-with"
              className="w-40"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {otherVersions.map((item) => (
                <SelectItem
                  key={item.version}
                  value={item.version.toString()}
                >
                  v{item.version}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {compared && (
            <span className="text-xs tabular-nums text-muted-foreground">
              <span className="text-emerald-700">
                +{summary.added}
              </span>{" "}
              <span className="text-red-700">
                −{summary.removed}
              </span>
            </span>
          )}
        </div>
        {compared && !hasChanges && (
          <p className="rounded-md border p-4 text-center text-sm text-muted-foreground">
            {t(`${editorPrefix}.diff.empty`)}
          </p>
        )}
        {compared && hasChanges && (
          <pre className="max-h-96 overflow-auto rounded-md border bg-muted/30 text-xs">
            {diff.map((line, index) => (
              <div
                key={`diff-line-${index}`}
                className={cn("flex gap-2 px-2 py-0.5", {
                  "bg-emerald-50 text-emerald-800":
                    line.type === "added",
                  "bg-red-50 text-red-800":
                    line.type === "removed",
                })}
              >
                <span className="w-8 shrink-0 select-none text-right tabular-nums text-muted-foreground">
                  {line.aLine ?? ""}
                </span>
                <span className="w-8 shrink-0 select-none text-right tabular-nums text-muted-foreground">
                  {line.bLine ?? ""}
                </span>
                <span className="w-3 shrink-0 select-none">
                  {line.type === "added"
                    ? "+"
                    : line.type === "removed"
                      ? "−"
                      : ""}
                </span>
                <span className="whitespace-pre-wrap break-all">
                  {line.content}
                </span>
              </div>
            ))}
          </pre>
        )}
      </DialogContent>
    </Dialog>
  )
}
